import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';

import { fieldHelper } from '@builttocreate/engine-utils';
import Icon from '../../../misc/Icon';
import ThemeIcons from '../../../../theme/Icons';
import ThemeContext from '../../../../theme/ThemeContext';
import { LightGrey } from '../../../../constants/Colors';
import FieldDragSourceTypes from '../../../../constants/FieldDragSourceTypes';
import FieldDisplayTypes from '../../../../constants/FieldDisplayTypes';
import FieldTypes from '../../../../constants/FieldTypes';

const { getMultiSelectFieldValue } = fieldHelper;

const Item = styled.div`
  padding: 8px;
  margin-left: ${(props) => props.level * 20}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  transition: opacity 0.1s ease-in-out;
  background-color: ${(props) => props.theme.backgroundColor || 'transparent'};
  color: ${(props) => props.theme.fontColorPrimary};
  margin-top: 2px;

  cursor: ${(props) => {
    if (props.isClone) return 'default';
    else return 'pointer';
  }};

  border-left: 0px;
  border-right: 0px;
  border-bottom: 1px solid ${(props) => props.theme.borderColor || LightGrey};
  border-radius: 6px;
  border-bottom-right-radius: ${(props) => (props.isActive || props.isSecondary ? 6 : 0)}px;
  border-bottom-left-radius: ${(props) => (props.isActive || props.isSecondary ? 6 : 0)}px;
  border: ${(props) => {
    if (props.isSecondary) return `2px solid ${props.theme.primaryOpaqueColor}`;
    if (props.isActive) return `2px solid ${props.theme.primaryColor}`;

    return '2px solid transparent';
  }};
  opacity: ${(props) => {
    if (props.isClone) return 0.7;
    return 1;
  }};
  
  svg {
    opacity: 1;
    width: 13px;
  }
  svg.fa-grip-vertical {
    display: none;
  }
  
  &:hover {
    background-color: ${(props) => {
    if (props.isClone) return 'transparent';
    else return props.theme.secondaryColor || 'transparent';
  }};
    cursor: ${(props) => {
    if (props.isClone) return 'default';
    else return 'grab';
  }};

    border-bottom-right-radius: 6px;
    border-bottom-left-radius: 6px;

    svg {
      display: ${(props) => (props.isClone ? 'inline' : 'none')};
      width: 13px;
    }

    svg.fa-grip-vertical {
      display: inline;
      opacity: 1;
      display: inline-block;
      width: 13px;
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.span`
  margin-left: 8px;
  font-size: 14px;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubContent = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-style: italic;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FieldPosition = ({
  fieldPosition,
  level,
  icon,
  activeFieldId,
  activeFieldFieldId,
  onFieldSelect,
  isClone,
}) => {

  const type = fieldPosition.type;
  const title = fieldPosition.title || type;
  const subtitle = getSubtitle(fieldPosition);
  const item = { ...fieldPosition, dragSource: FieldDragSourceTypes.tree };
  const theme = useContext(ThemeContext);
  const [collectedProps, drag] = useDrag({ type, item });
  const isActive = activeFieldId === fieldPosition._id;
  const isSecondaryActive = !isActive && fieldPosition.field === activeFieldFieldId;

  return (
    <Item
      level={level}
      theme={theme}
      id={`fieldposition-list-item-${type}`}
      ref={isClone ? null : drag}
      isActive={isActive}
      isSecondary={isSecondaryActive}
      {...collectedProps}
      data-testid={`fieldposition-list-item-${type}`}
      onClick={() => {
        if (isClone) return;
        onFieldSelect(fieldPosition.field, fieldPosition.identifier, fieldPosition._id, fieldPosition.rowId, fieldPosition.column, fieldPosition.columnIdentifier);
      }}
      isClone={isClone}
    >
      <ContentWrapper>
        <Icon color={theme.fontColorPrimary} icon={icon} size="sm" />
        {!isClone && (
          <Icon
            color={theme.fontColorPrimary}
            icon={ThemeIcons.misc.drag}
          />
        )}

        <TitleWrapper>
          <Content>{title}</Content>
          {subtitle && <SubContent>{subtitle}</SubContent>}
        </TitleWrapper>
      </ContentWrapper>
    </Item>
  );
};

const getSubtitle = (fieldPosition) => {
  if (fieldPosition.type === FieldTypes.table) {
    return fieldPosition.displayType !== FieldDisplayTypes.original ? 'cell' : null;
  } else if (fieldPosition.type === FieldTypes.block) {
    return fieldPosition.value ? fieldPosition.value : null;
  } else if (fieldPosition.type === FieldTypes.multiSelect) {
    const selectedOptions = getMultiSelectFieldValue(fieldPosition.value);
    const options = fieldPosition.options ? fieldPosition.options.filter((o) => !o.deleted) : [];
    const optionsValues = selectedOptions ? options?.map((option) => {
      const selectedOption = selectedOptions.find((o) => o === option._id);
      return selectedOption ? option.value : null;
    }) : [];

    return optionsValues ? optionsValues[0] : null;
  } else {
    return null;
  }
};

FieldPosition.propTypes = {
  fieldPosition: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    _id: PropTypes.string.isRequired
  }).isRequired,
  level: PropTypes.number.isRequired,
  icon: PropTypes.object,
  activeFieldId: PropTypes.string
};

export default FieldPosition;